import { findItemByIndex, IconButton, RichTextFormatData, SuggestionsGroupData, SuggestionsItemActionsComponent, SuggestionsItemData, SuggestionsList } from "@xapp/chat-widget-core";
import "@xapp/chat-widget-core/dist/index.css";
import React, { FC, MouseEvent, useCallback, useEffect, useMemo, useState } from "react";
import { LeftDownArrowIcon } from "../Icons";
import "./Suggestions.scss";
import { MarkdownBuilder } from "../MardownBuilder";

export interface SuggestionsProps {
    readonly data: readonly SuggestionsGroupData[];
    readonly index: number;
    readonly className?: string;
    readonly searchTerms?: string;
    readonly hasWsButton?: boolean;
    onItemClick(data: SuggestionsItemData): void;
    onItemUse(data: SuggestionsItemData): void;
}

function createActions(onItemUse: (data: SuggestionsItemData) => void): SuggestionsItemActionsComponent {
    return innerProps => {
        const { data } = innerProps;
        const canUse = data.type !== "FAQ";

        const handleUse = useCallback((ev: MouseEvent<HTMLDivElement>) => {
            onItemUse(data);
            ev.stopPropagation();
        }, [data]);

        if (!canUse) {
            return <></>
        };

        return (
            <IconButton
                className="xappw-suggestions__use"
                onClick={handleUse}
                icon={LeftDownArrowIcon}
            />
        );
    };
}

export const Suggestions: FC<SuggestionsProps> = props => {
    const { data, onItemUse, searchTerms } = props;
    const [fixedSuggestions, setFixedSuggestions] = useState<readonly SuggestionsGroupData[]>();
    const [activeItem, setActiveItem] = useState<SuggestionsItemData | undefined>();

    useEffect(() => {
        if (data) {
            setFixedSuggestions(data);
        }
        if (!searchTerms) {
            setFixedSuggestions(undefined);
            setActiveItem(undefined);
        }
    }, [data, searchTerms])

    const len = data?.length;
    const currentIndex = len > 0 ? props.index : NaN;

    const item = useMemo(() => findItemByIndex(data, currentIndex), [data, currentIndex]);

    const handleSpanClick = useCallback((target: SuggestionsItemData, span: RichTextFormatData) => {
        if (span.type === "inputText") {
            onItemUse(target);
            setActiveItem(undefined);
            return false;
        }
        return undefined;
    }, [onItemUse]);

    useEffect(() => {
        setActiveItem(item);
    }, [item]);

    const actions: SuggestionsItemActionsComponent = useMemo<SuggestionsItemActionsComponent>(() => createActions(onItemUse), [onItemUse]);

    const transformContent = (content: string): JSX.Element[] => {
        const lines = content.split('\n');
        return lines.map(line => {
            if (line) {
                return <MarkdownBuilder text={line} />
            }
            return <></>
        })
    };

    const containerClass = `xappw-suggestions ${props.className || ""} ${props.hasWsButton ? "xappw-suggestions--with-ws-button" : ""}`.trim();

    return (
        <div className={containerClass}>
            {activeItem?.content && <div className="xappw-suggestions__answer">{transformContent(activeItem.content)}</div>}
            {
                fixedSuggestions && fixedSuggestions.length > 0 &&
                <SuggestionsList
                    suggestions={fixedSuggestions}
                    index={currentIndex}
                    className="xappw-suggestions__groups"
                    itemActions={actions}
                    onItemClick={props.onItemClick}
                    onItemHover={setActiveItem}
                    onSpanClick={handleSpanClick}
                />
            }
        </div>
    )
};
